const chapter = {
  id: 'body-fluids-circulation',
  slug: 'body-fluids-circulation',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Body Fluids and Circulation',
        hindi: 'शरीर द्रव और परिसंचरण',
  },
  estReadMinutes: 55,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3-4 questions in NEET',
        hindi: 'NEET में 3-4 प्रश्न',
  },
  syllabusTags: ['Human Physiology', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'blood',
      title: {
        english: 'Blood — Composition',
        hindi: 'रक्त — संघटन',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Blood is a fluid connective tissue (~5-6 L in adult). pH 7.35-7.45 (slightly alkaline). Composed of plasma (55 %) + formed elements (45 %).',
        hindi: 'रक्त एक तरल संयोजी ऊतक है (वयस्कों में ~5-6 लीटर)। पीएच 7.35-7.45 (थोड़ा क्षारीय)। प्लाज्मा (55%) + गठित तत्वों (45%) से बना है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Plasma** — straw-coloured. 90-92 % water; 6-8 % proteins (**albumin** — osmotic balance, **globulins** — antibodies, **fibrinogen** — clotting). Also salts, glucose, hormones, gases, wastes.',
        hindi: '**प्लाज्मा** - भूसे के रंग का। 90-92% पानी; 6-8% प्रोटीन (**एल्ब्यूमिन**-ऑस्मोटिक संतुलन, **ग्लोबुलिन**-एंटीबॉडीज़, **फाइब्रिनोजेन**-थक्का बनाना)। इसके अलावा लवण, ग्लूकोज, हार्मोन, गैसें, अपशिष्ट।',
            },
            {
              english: '**Serum** = plasma without fibrinogen.',
        hindi: '**सीरम** = फ़ाइब्रिनोजेन के बिना प्लाज्मा।',
            },
            {
              english: '**RBCs (erythrocytes)** — 5-5.5 million/mm³ in males, 4.5-5 million in females. Biconcave, **enucleated** (in mammals); ~120 day lifespan; formed in red bone marrow (erythropoiesis); destroyed in spleen ("graveyard of RBCs"). Contain **haemoglobin** (12-16 g/dL) — Fe-porphyrin protein.',
        hindi: '**आरबीसी (एरिथ्रोसाइट्स)** - पुरुषों में 5-5.5 million/mm³, महिलाओं में 4.5-5 मिलियन। उभयलिंगी, **एन्यूक्लिएटेड** (स्तनधारियों में); ~120 दिन का जीवनकाल; लाल अस्थि मज्जा (एरिथ्रोपोइज़िस) में गठित; प्लीहा में नष्ट हो गया ("आरबीसी का कब्रिस्तान")। इसमें **हीमोग्लोबिन** (12-16 g/dL) - Fe-पोर्फिरिन प्रोटीन होता है।',
            },
            {
              english: '**WBCs (leucocytes)** — 6000-8000/mm³; nucleated; lifespan few days. Two groups:',
        hindi: '**डब्ल्यूबीसी (ल्यूकोसाइट्स)** — 6000-8000/mm³; न्यूक्लियेटेड; जीवन काल कुछ दिन. दो समूह:',
            },
            {
              english: '• **Granulocytes** — Neutrophils (60-65 % most abundant; phagocytic), Eosinophils (2-3 %; allergy & parasites), Basophils (0.5-1 % rarest; release histamine, heparin).',
        hindi: '• **ग्रैनुलोसाइट्स** - न्यूट्रोफिल (60-65% सबसे प्रचुर; फागोसाइटिक), इओसिनोफिल्स (2-3%; एलर्जी और परजीवी), बेसोफिल्स (0.5-1% दुर्लभ; हिस्टामाइन, हेपरिन जारी करते हैं)।',
            },
            {
              english: '• **Agranulocytes** — Lymphocytes (20-25 %; B & T cells — immunity), Monocytes (6-8 % largest; phagocytic).',
        hindi: '• **एग्रानुलोसाइट्स** - लिम्फोसाइट्स (20-25%; बी एंड टी कोशिकाएं - प्रतिरक्षा), मोनोसाइट्स (6-8% सबसे बड़ा; फागोसाइटिक)।',
            },
            {
              english: '**Platelets (thrombocytes)** — 1.5-3.5 lakh/mm³. Fragments of megakaryocytes; involved in clotting. < 1 lakh = thrombocytopenia.',
        hindi: '**प्लेटलेट्स (थ्रोम्बोसाइट्स)** — 1.5-3.5 lakh/mm³। मेगाकार्योसाइट्स के टुकड़े; थक्के जमने में शामिल. <1 lakh = thrombocytopenia.',
            },
          ],
        },
      ],
    },
    {
      id: 'blood-groups-clotting',
      title: {
        english: 'Blood Groups & Clotting',
        hindi: 'रक्त समूह और थक्का बनना',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**ABO system** (Karl Landsteiner, 1900) — based on antigens A & B on RBCs:',
        hindi: '**एबीओ प्रणाली** (कार्ल लैंडस्टीनर, 1900) - आरबीसी पर एंटीजन ए और बी पर आधारित:',
            },
            {
              english: '• A → A antigen, anti-B antibody.',
        hindi: '• ए → ए एंटीजन, एंटी-बी एंटीबॉडी।',
            },
            {
              english: '• B → B antigen, anti-A antibody.',
        hindi: '• बी → बी एंटीजन, एंटी-ए एंटीबॉडी।',
            },
            {
              english: '• AB → both antigens, no antibody (universal recipient).',
        hindi: '• एबी → दोनों एंटीजन, कोई एंटीबॉडी नहीं (सार्वभौमिक प्राप्तकर्ता)।',
            },
            {
              english: '• O → no antigen, both antibodies (universal donor).',
        hindi: '• O → कोई एंटीजन नहीं, दोनों एंटीबॉडी (सार्वभौमिक दाता)। **Rh प्रणाली** — Rh एंटीजन (रीसस बंदर के नाम पर)। Rh⁺ में एंटीजन होता है, Rh⁻ में नहीं। बेमेल **एरिथ्रोब्लास्टोसिस फीटेलिस** (आरएच⁻ मां, आरएच⁺ भ्रूण - एंटी-आरएच एंटीबॉडीज बाद के गर्भधारण में भ्रूण के आरबीसी को नष्ट कर देते हैं) का कारण बन सकता है। रोकथाम: पहली डिलीवरी के 72 घंटे के भीतर एंटी-आरएच इंजेक्शन। **रक्त का थक्का जमना (जमावट)** — कैस्केड:',
            },
            {
              english: '**Rh system** — Rh antigen (named after Rhesus monkey). Rh⁺ has antigen, Rh⁻ does not. Mismatch can cause **erythroblastosis foetalis** (Rh⁻ mother, Rh⁺ foetus — anti-Rh antibodies destroy foetal RBCs in subsequent pregnancies). Prevention: anti-Rh injection within 72 h of first delivery.',
        hindi: '**आरएच प्रणाली** - आरएच एंटीजन (रीसस बंदर के नाम पर)। Rh⁺ में एंटीजन होता है, Rh⁻ में नहीं। बेमेल **एरिथ्रोब्लास्टोसिस फीटेलिस** (आरएच⁻ मां, आरएच⁺ भ्रूण - एंटी-आरएच एंटीबॉडीज बाद के गर्भधारण में भ्रूण के आरबीसी को नष्ट कर देते हैं) का कारण बन सकता है। रोकथाम: पहली डिलीवरी के 72 घंटे के भीतर एंटी-आरएच इंजेक्शन।',
            },
            {
              english: '**Blood clotting (coagulation)** — cascade:',
        hindi: '**रक्त का थक्का जमना (जमावट)** - झरना:',
            },
            {
              english: '• Platelets at injury release **thromboplastin** (factor III).',
        hindi: '• चोट लगने पर प्लेटलेट्स **थ्रोम्बोप्लास्टिन** (कारक III)।',
            },
            {
              english: '• Thromboplastin + Ca²⁺ + factors → activates **prothrombin → thrombin**.',
        hindi: '• थ्रोम्बोप्लास्टिन + Ca²⁺ + कारक → **प्रोथ्रोम्बिन → थ्रोम्बिन** को सक्रिय करता है।',
            },
            {
              english: '• Thrombin converts soluble **fibrinogen → fibrin** (insoluble threads) → traps cells → clot.',
        hindi: '• थ्रोम्बिन घुलनशील **फाइब्रिनोजेन → फाइब्रिन** (अघुलनशील धागे) → Common Trap कोशिकाओं → थक्के को परिवर्तित करता है।',
            },
            {
              english: '• **Vitamin K** is essential for prothrombin synthesis in liver.',
        hindi: '• **विटामिन K** लीवर में प्रोथ्रोम्बिन संश्लेषण के लिए आवश्यक है।',
            },
          ],
        },
      ],
    },
    {
      id: 'lymph',
      title: {
        english: 'Lymph & Tissue Fluid',
        hindi: 'लसिका और ऊतक द्रव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Tissue fluid (interstitial fluid)** — leaks out of capillaries; minus plasma proteins and RBCs.',
        hindi: '**ऊतक द्रव (अंतरालीय द्रव)** - केशिकाओं से रिसना; माइनस प्लाज्मा प्रोटीन और आरबीसी।',
            },
            {
              english: '**Lymph** — collected by lymph capillaries → vessels → ducts (right & thoracic) → returned to subclavian veins. Has lymphocytes & WBCs but no RBCs/platelets; less protein.',
        hindi: '**लसीका** - लसीका केशिकाओं → वाहिकाओं → नलिकाओं (दाहिनी और वक्षीय) → सबक्लेवियन नसों द्वारा एकत्र किया जाता है। इसमें लिम्फोसाइट्स और WBCs हैं लेकिन कोई RBCs/platelets नहीं है; कम प्रोटीन.',
            },
            {
              english: 'Functions: returns fluid, transports fats from intestine (via **lacteals** in villi as chylomicrons), defence (lymph nodes filter pathogens).',
        hindi: 'कार्य: तरल पदार्थ लौटाता है, आंत से वसा का परिवहन करता है (विली में काइलोमाइक्रोन के रूप में **लैक्टियल्स** के माध्यम से), रक्षा (लिम्फ नोड्स रोगजनकों को फ़िल्टर करता है)।',
            },
          ],
        },
      ],
    },
    {
      id: 'heart',
      title: {
        english: 'Human Heart',
        hindi: 'मानव हृदय',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Mesodermal; size of clenched fist (~300 g); located in mediastinum between lungs; protected by **pericardium** (double membrane with pericardial fluid).',
        hindi: 'मेसोडर्मल; बंद मुट्ठी का आकार (~300 ग्राम); फेफड़ों के बीच मीडियास्टिनम में स्थित; **पेरीकार्डियम** (पेरीकार्डियल द्रव के साथ दोहरी झिल्ली) द्वारा संरक्षित।',
            },
            {
              english: '**Four-chambered**: 2 atria (upper, thin-walled) + 2 ventricles (lower, thick-walled, especially left).',
        hindi: '**चार-कक्षीय**: 2 अटरिया (ऊपरी, पतली दीवार वाली) + 2 निलय (निचली, मोटी दीवार वाली, विशेष रूप से बाईं ओर)।',
            },
            {
              english: '**Valves**:',
        hindi: '**वाल्व**:',
            },
            {
              english: '• **Tricuspid** — between right atrium & ventricle.',
        hindi: '• **ट्राइकसपिड** - दाएं आलिंद और निलय के बीच।',
            },
            {
              english: '• **Bicuspid (mitral)** — between left atrium & ventricle.',
        hindi: '• **बाइसपिड (माइट्रल)** - बाएं आलिंद और निलय के बीच।',
            },
            {
              english: '• **Semilunar valves** — at openings of pulmonary artery & aorta.',
        hindi: '• **सेमिलुनर वाल्व** - फुफ्फुसीय धमनी और महाधमनी के उद्घाटन पर।',
            },
            {
              english: 'Valves prevent **backflow** of blood.',
        hindi: 'वाल्व रक्त के **बैकफ़्लो** को रोकते हैं। **संचालन प्रणाली** (मायोजेनिक - हृदय अपने आप सिकुड़ता है):',
            },
            {
              english: '**Conducting system** (myogenic — heart contracts on its own):',
        hindi: '**संचालन प्रणाली** (मायोजेनिक - हृदय अपने आप सिकुड़ता है):',
            },
            {
              english: '• **SA node (Sino-Atrial)** in right atrium — **pacemaker** (~70-75 impulses/min).',
        hindi: '• **SA नोड (सिनो-एट्रियल)** दाहिने आलिंद में - **पेसमेकर** (~70-75 impulses/min)।',
            },
            {
              english: '• AV node — receives impulses; transmits to Bundle of His → bundle branches → Purkinje fibres → ventricular contraction.',
        hindi: '• एवी नोड - आवेग प्राप्त करता है; उसके बंडल → बंडल शाखाएं → पर्किनजे फाइबर → वेंट्रिकुलर संकुचन तक पहुंचाता है। **हृदय चक्र** - एक धड़कन के लिए ~0.8 सेकेंड (75 बीपीएम):',
            },
            {
              english: '**Cardiac cycle** — ~0.8 s for one beat (75 bpm):',
        hindi: '**हृदय चक्र** - एक धड़कन के लिए ~0.8 सेकंड (75 बीपीएम):',
            },
            {
              english: '• Atrial systole 0.1 s; ventricular systole 0.3 s; joint diastole 0.4 s.',
        hindi: '• आलिंद सिस्टोल 0.1 एस; वेंट्रिकुलर सिस्टोल 0.3 एस; संयुक्त डायस्टोल 0.4 एस.',
            },
            {
              english: '• Two heart sounds: **"lubb"** (closure of AV valves at start of ventricular systole), **"dubb"** (closure of semilunar valves at end of ventricular systole).',
        hindi: '• दो हृदय ध्वनियाँ: **"लब"** (वेंट्रिकुलर सिस्टोल की शुरुआत में एवी वाल्व का बंद होना), **"डब"** (वेंट्रिकुलर सिस्टोल के अंत में सेमीलुनर वाल्व का बंद होना)।',
            },
            {
              english: '**Cardiac output** = stroke volume × heart rate ≈ 70 mL × 72 = **~5 L/min**.',
        hindi: '**हृदय आउटपुट** = स्ट्रोक की मात्रा × हृदय गति ≈ 70 एमएल × 72 = **~5 L/min**।',
            },
            {
              english: '**ECG (electrocardiogram)** — Einthoven. **P wave** = atrial depolarisation (excitation); **QRS complex** = ventricular depolarisation (start of ventricular systole); **T wave** = ventricular repolarisation (end of systole). Interval between QRS = heart rate.',
        hindi: '**ईसीजी (इलेक्ट्रोकार्डियोग्राम)** - एंथोवेन। **पी तरंग** = आलिंद विध्रुवण (उत्तेजना); **क्यूआरएस कॉम्प्लेक्स** = वेंट्रिकुलर डीपोलराइजेशन (वेंट्रिकुलर सिस्टोल की शुरुआत); **टी तरंग** = वेंट्रिकुलर रिपोलराइजेशन (सिस्टोल का अंत)। QRS = heart rate के बीच अंतराल।',
            },
          ],
        },
      ],
    },
    {
      id: 'circulation-disorders',
      title: {
        english: 'Circulatory Pathways & Disorders',
        hindi: 'परिसंचरण पथ और विकार',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Double circulation** in mammals/birds:',
        hindi: '**डबल सर्कुलेशन** mammals/birds में:',
            },
            {
              english: '• **Pulmonary** — RV → pulmonary artery → lungs → pulmonary vein → LA. Carries deoxygenated → oxygenated.',
        hindi: '• **फुफ्फुसीय** — आरवी → फुफ्फुसीय धमनी → फेफड़े → फुफ्फुसीय शिरा → एलए। ऑक्सीजन रहित → ऑक्सीजन युक्त ले जाता है।',
            },
            {
              english: '• **Systemic** — LV → aorta → tissues → vena cavae → RA.',
        hindi: '• **प्रणालीगत** — एलवी → महाधमनी → ऊतक → वेना कावे → आरए।',
            },
            {
              english: '**Hepatic portal system** — between intestine & liver; carries digested food.',
        hindi: '**हेपेटिक पोर्टल प्रणाली** - आंत और यकृत के बीच; पचे हुए भोजन को ले जाता है।',
            },
            {
              english: '**Coronary circulation** — supplies heart muscle itself.',
        hindi: '**कोरोनरी सर्कुलेशन** - हृदय की मांसपेशियों की आपूर्ति स्वयं करता है। **विनियमन**: मेडुला ऑबोंगटा + स्वायत्त तंत्रिकाएं। सहानुभूतिपूर्ण ↑ दर; पैरासिम्पेथेटिक (वेगस) ↓ दर। एड्रेनालाईन ↑. **विकार**:',
            },
            {
              english: '**Regulation**: medulla oblongata + autonomic nerves. Sympathetic ↑ rate; parasympathetic (vagus) ↓ rate. Adrenaline ↑.',
        hindi: '**विनियमन**: मेडुला ऑबोंगटा + स्वायत्त तंत्रिकाएँ। सहानुभूतिपूर्ण ↑ दर; पैरासिम्पेथेटिक (वेगस) ↓ दर। एड्रेनालाईन ↑.',
            },
            {
              english: '**Disorders**:',
        hindi: '**विकार**:',
            },
            {
              english: '• **Hypertension** — BP > 140/90 (normal 120/80). Risk for heart disease, stroke.',
        hindi: '• **उच्च रक्तचाप** — BP > 140/90 (सामान्य 120/80)। हृदय रोग, स्ट्रोक का खतरा।',
            },
            {
              english: '• **Coronary artery disease (CAD) / atherosclerosis** — narrowing of coronary arteries by plaques.',
        hindi: '• **कोरोनरी धमनी रोग (सीएडी) / एथेरोस्क्लेरोसिस** - प्लाक द्वारा कोरोनरी धमनियों का संकुचित होना।',
            },
            {
              english: '• **Angina pectoris** — chest pain due to ↓ O₂ to heart.',
        hindi: '• **एनजाइना पेक्टोरिस** — हृदय के ↓O₂ के कारण सीने में दर्द।',
            },
            {
              english: '• **Heart failure** — heart unable to pump enough.',
        hindi: '• **हृदय विफलता** — हृदय पर्याप्त पंप करने में असमर्थ।',
            },
            {
              english: '• **Cardiac arrest** — heart stops beating.',
        hindi: '• **हृदय गति रुकना** - दिल धड़कना बंद कर देता है।',
            },
            {
              english: '• **Myocardial infarction** — heart attack; damage of heart muscle.',
        hindi: '• **मायोकार्डियल रोधगलन** — दिल का दौरा; हृदय की मांसपेशियों की क्षति.',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Blood pH} = 7.35\\text{-}7.45',
      desc: {
        english: '',
        hindi: 'रक्त पीएच = 7.35-7.45',
      },
    },
    {
      latex: '\\text{RBC: 5 million/mm}^3 \\text{ (M); 4.5 million (F)}',
      desc: {
        english: '',
        hindi: 'आरबीसी: 5 मिलियन/मिमी 3 (एम); 4.5 मिलियन (एफ)',
      },
    },
    {
      latex: '\\text{Cardiac output} = SV \\times HR \\approx 70 \\times 72 = 5\\ \\text{L/min}',
      desc: {
        english: '',
        hindi: 'कार्डिएक आउटपुट = एसवी एचआर 70 72 = 5\\ एल/मिनट',
      },
    },
    {
      latex: '\\text{ECG: P (atrial), QRS (ventricular), T (repolarisation)}',
      desc: {
        english: '',
        hindi: 'ईसीजी: पी (एट्रियल), क्यूआरएस (वेंट्रिकुलर), टी (रिपोलराइजेशन)',
      },
    },
    {
      latex: '\\text{SA node} \\to \\text{AV node} \\to \\text{Bundle of His} \\to \\text{Purkinje}',
      desc: {
        english: '',
        hindi: 'उनके पर्किनजे का एसए नोड एवी नोड बंडल',
      },
    },
    {
      latex: '\\text{Fibrinogen} \\xrightarrow{\\text{thrombin}} \\text{Fibrin (clot)}',
      desc: {
        english: '',
        hindi: 'फाइब्रिनोजेन फाइब्रिन (थक्का)',
      },
    },
    {
      latex: '\\text{O = universal donor; AB = universal recipient}',
      desc: {
        english: '',
        hindi: 'ओ = सार्वभौमिक दाता; एबी = सार्वभौमिक प्राप्तकर्ता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'ECG',
      problem: {
        english: 'QRS complex in ECG represents:',
        hindi: 'ECG में QRS कॉम्प्लेक्स दर्शाता है:',
      },
      answer: {
        english: 'Ventricular depolarisation',
        hindi: 'वेंट्रिकुलर विध्रुवण',
      },
      solution: {
        english: 'Marks beginning of ventricular systole.',
        hindi: 'वेंट्रिकुलर सिस्टोल की शुरुआत का प्रतीक है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Heart',
      problem: {
        english: 'Pacemaker of human heart is:',
        hindi: 'मानव हृदय का पेसमेकर है:',
      },
      answer: {
        english: 'SA node',
        hindi: 'एसए नोड',
      },
      solution: {
        english: 'Generates ~75 impulses/min.',
        hindi: '~75 impulses/min उत्पन्न करता है।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Blood',
      problem: {
        english: 'Universal recipient blood group is:',
        hindi: 'सार्वभौमिक प्राप्तकर्ता रक्त समूह है:',
      },
      answer: {
        english: 'AB',
        hindi: 'एबी',
      },
      solution: {
        english: 'No antibodies; can receive all groups.',
        hindi: 'कोई एंटीबॉडी नहीं; सभी समूह प्राप्त कर सकते हैं.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Clotting',
      problem: {
        english: 'Vitamin essential for synthesis of prothrombin in liver:',
        hindi: 'लीवर में प्रोथ्रोम्बिन के संश्लेषण के लिए आवश्यक विटामिन:',
      },
      answer: {
        english: 'Vitamin K',
        hindi: 'विटामिन K',
      },
      solution: {
        english: 'Deficiency → bleeding disorders.',
        hindi: 'कमी → रक्तस्राव विकार।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Rh',
      problem: {
        english: 'Erythroblastosis foetalis occurs when:',
        hindi: 'एरिथ्रोब्लास्टोसिस फेटेलिस तब होता है जब:',
      },
      answer: {
        english: 'Rh⁻ mother bears Rh⁺ foetus (subsequent pregnancy)',
        hindi: 'Rh⁻ माँ Rh⁺ भ्रूण को जन्म देती है (बाद में गर्भावस्था)',
      },
      solution: {
        english: 'Anti-Rh antibodies cross placenta and destroy foetal RBCs.',
        hindi: 'एंटी-आरएच एंटीबॉडीज प्लेसेंटा को पार करती हैं और भ्रूण की आरबीसी को नष्ट कर देती हैं।',
      },
    },
  ],
  next: {
    slug: 'excretory-products',
    title: 'Excretory Products and their Elimination',
  },
};

export default chapter;
